import { Product, ProductPrice } from "@/api/product-api";
import {
  getSetDeal,
  SET_VARIANT_BY_SLUG,
  SetDeal,
} from "@/libs/product/set-variant";
import { canPackSet } from "@/libs/cart/pack-set-lines";
import { useCurrency } from "@/hooks/useCurrency";
import { cn } from "@/libs/utils";
import { useProducts } from "@/services/product/useProductService";
import { useLocale } from "@/store/locale-store";
import { useMemo } from "react";
import { useCartWithProducts } from "@/store/useCartWithProducts";
import { useGeDisplayMarked } from "@/contexts/cart-ge-eligibility";
import { formatMoney } from "@/utils/CommonUtils";
import { useTrans } from "@/utils/useTrans";
import { GePrice } from "@/v2-components/GePrice";
import { Badge } from "@/v2-components/ui/badge";
import { localeDependentProps } from "@/libs/locale/locale-ready";
import { geTextProps } from "@/libs/global-e/marker";
import { RailCardPrice } from "./rail-card-price";

const saleOf = (p: ProductPrice) => p.originalAmount - p.discountAmount;

/**
 * 단품의 **수량 세트 거래** — 짝(`getSetDeal`)과 두 가격을 함께 돌려준다. 거래가 성립하지
 * 않으면 `null`(세트 없음 · 어느 한쪽 가격 없음 · 세트 재고 0 · 세트가 더 비싸거나 같음).
 *
 * ⚠️ 거래 성립 여부는 결제 쪽 `packSetLines` 와 **같은 술어**(`canPackSet`)로 판정한다 —
 * 갈리면 화면은 세트가를 약속하는데 결제는 단품가로 나간다(또는 그 반대).
 *
 * 가격은 `useCartWithProducts().getProductPrice` 로 구한다 — 레일 카드(`RailCardPrice`)와
 * 카트가 쓰는 함수라 티어·레퍼럴 할인이 같은 값으로 나온다.
 *
 * ★절감률은 **세트의 MSRP 마크다운이 아니라 「단품 N개를 따로 살 때」 대비**다 —
 * 사용자가 실제로 비교하는 대상이 그것이다(`bundle-merger.ts` 의 `componentTotal` 과 같은 기준).
 * 반올림은 **사이트 공통(`useProductPrice` 의 `Math.round`)에 맞춘다** — 세트를 고르면 상단
 * 가격 배지(`TactSuitCard.PriceDetail`)가 그 함수로 그려지는데, 여기만 내림하면 한 화면에
 * 두 숫자가 뜬다. 실측(2026-10-02 · 3002 · US 티어 단품 $17 · 세트 $34−$7): 내림일 때
 * 상단 「21% off」 · 스와치 「-20%」였다.
 */
export function useSetDeal(baseSlug: string): {
  deal: SetDeal;
  basePrice: ProductPrice;
  setPrice: ProductPrice;
  savePercent: number;
} | null {
  const { data: products } = useProducts();
  const { getProductPrice } = useCartWithProducts();
  const currency = useCurrency();
  const { countryCode } = useLocale();
  const deal = getSetDeal(products ?? [], baseSlug);
  if (!deal || !canPackSet(deal, { currency, countryCode })) return null;

  // 표시가는 레퍼럴 할인까지 반영한 값(카트와 같은 함수). 판정은 위에서 끝났다.
  const basePrice = getProductPrice(deal.base);
  const setPrice = getProductPrice(deal.set);
  if (!basePrice || !setPrice) return null;

  const separately = saleOf(basePrice) * deal.units;
  const savePercent = Math.round((1 - saleOf(setPrice) / separately) * 100);
  return { deal, basePrice, setPrice, savePercent };
}

/**
 * **목록에서 세트를 대표 카드로 세울 단품들**(#6008) — `ProductUtil.categorize*Accessories` 의
 * `setFirstBases` 로 넘긴다. 판정은 PDP 기본 선택(`useSetDeal`)·카트 묶기와 **같은 술어**(`canPackSet`)다 —
 * 목록은 세트를 내세웠는데 PDP 는 단품으로 열리거나, 담은 세트가 카트에서 단품으로 풀리는 갈림을 막는다.
 *
 * `products` 는 목록이 실제로 그리는 배열을 받는다(가격 필터 뒤) — 세트가 걸러졌으면 `getSetDeal` 이
 * 못 찾아 단품 카드로 남는다.
 */
export function useSetFirstBases(products: Product[]): ReadonlySet<string> {
  const currency = useCurrency();
  const { countryCode } = useLocale();
  return useMemo(
    () =>
      new Set(
        Object.keys(SET_VARIANT_BY_SLUG).filter((baseSlug) => {
          const deal = getSetDeal(products, baseSlug);
          return !!deal && canPackSet(deal, { currency, countryCode });
        }),
      ),
    [products, currency, countryCode],
  );
}

/**
 * 단품 PDP 의 「단품 / N개 세트」 선택 (MOLLE Pouch) — **가격 카드 두 줄**이다.
 *
 * 요청 시안(2026-10-06 · 사용자 공유 이미지): 카드마다 「라벨 / 가격」, 세트 카드엔 「단품 N개」 정가
 * 줄긋기와 빨간 `-N%` 배지. 모양은 같은 레일의 DK3 글러브 「Bundle Set」 행(`TactGloveOptions` —
 * 64px · 파란 2px 외곽선 · 우측 빨간 배지)을 그대로 따른다. 종전(#5906)엔 색상 스와치처럼 64px 사진
 * 두 장 + 「Pack: 값」 헤더였다 — 사진은 이제 세트를 고르면 **갤러리**가 세트 제품 사진으로 바꿔 보여 주고
 * (`displayProduct` = 세트), 라벨이 카드 안에 있어 헤더도 뺐다.
 *
 * 라벨은 「Quantity」가 아니라 「Single / Set of N」이다 — 수량으로 읽으면 「Quantity: 2」를 두 번 담아
 * 세트 2개(파우치 4개)가 되는 오해가 생긴다.
 *
 * ★할인은 **두 곳**에서 보인다. 고르기 전 — 세트 카드의 정가 줄긋기·`-N%` 배지. 고른 뒤 — BuyBox 상단
 * 가격(`TactSuitCard.PriceDetail`)이 `checkoutProduct`(= 세트)의 할인가·정가·배지로 바뀐다.
 *
 * 세트를 고르면 페이지가 스토어 제품을 **세트 제품으로 바꾼다** — 그러면 `getSku()` 가 세트
 * SKU 를 내고, `checkoutProduct`(SKU 소유 제품)가 세트가 되어 제품명·가격·담기·결제가 전부
 * 세트 기준이 된다. 글러브 `all-size` 와 같은 「SKU 가 다른 제품 소속」 구조다.
 */
export function SetOptionSelect({
  baseSlug,
  setChosen,
  onChange,
}: {
  baseSlug: string;
  setChosen: boolean;
  onChange: (setChosen: boolean) => void;
}) {
  const { t } = useTrans(["shop"]);
  const { countryCode } = useLocale();
  const setDeal = useSetDeal(baseSlug);
  if (!setDeal) return null;
  const { deal, basePrice, setPrice, savePercent } = setDeal;

  // 세트가 위 — 세트가 기본 선택이고 단품은 대안이다(#6008). 단품 카드엔 줄긋기가 없으니 비교는
  // 세트 카드의 「단품 N개」 정가가 맡는다.
  const rows = [
    {
      value: true,
      label: t("Set of {{count}}", { count: deal.units }),
      amount: saleOf(setPrice),
      currency: setPrice.currency,
      separately: saleOf(basePrice) * deal.units,
      badge: savePercent > 0 ? `-${savePercent}%` : null,
    },
    {
      value: false,
      label: t("Single"),
      amount: saleOf(basePrice),
      currency: basePrice.currency,
      // 단품은 줄긋기 없이 가격만 — 비교 대상은 세트 카드의 「단품 N개」다.
      separately: null as number | null,
      badge: null as string | null,
    },
  ];

  return (
    <div role="radiogroup" className="flex flex-col gap-2">
      {rows.map((row) => {
        const chosen = row.value === setChosen;
        return (
          <button
            key={String(row.value)}
            type="button"
            role="radio"
            aria-checked={chosen}
            aria-label={row.badge ? `${row.label} (${row.badge})` : row.label}
            onClick={() => onChange(row.value)}
            className={cn(
              "flex h-16 w-full items-center justify-between rounded border border-coolgray-20 bg-white px-3 text-left transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-60 focus-visible:ring-offset-2",
              chosen
                ? "outline outline-2 -outline-offset-2 outline-blue-60"
                : "hover:border-coolgray-90",
            )}
          >
            <div>
              <span className="text-base tracking-[0.25px]">{row.label}</span>
              <div className="flex items-center gap-1">
                <span className="text-lg font-600 tracking-[0.5px]">
                  <GePrice
                    amount={row.amount}
                    currency={row.currency}
                    countryCode={countryCode}
                  />
                </span>
                {row.separately !== null && row.separately > row.amount && (
                  <span className="text-lg tracking-[0.5px] text-coolgray-50 line-through">
                    <GePrice
                      amount={row.separately}
                      currency={row.currency}
                      countryCode={countryCode}
                    />
                  </span>
                )}
              </div>
            </div>
            {row.badge && (
              <div className="flex h-9 min-w-[50px] items-center justify-center rounded bg-red-60 px-2 text-lg font-600 text-white">
                {row.badge}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * 장비 PDP 액세서리 행의 가격 — 세트 거래가 있는 단품이면 **수량에 맞춰** 세트가를 보여준다.
 *
 * 수량 N 을 고르면 실제 청구는 `packSetLines` 가 「세트 ⌊N/units⌋ + 단품 나머지」로 접는다.
 * 여기 표시도 **같은 산식**이어야 화면과 결제가 갈리지 않는다. 미선택이거나 N < units 면
 * 단가 표시(`RailCardPrice`) 그대로 두고, 아래에 「N개 구매 시 X% 할인」만 안내한다.
 */
export function SetDealRailPrice({
  product,
  quantity,
  selected,
}: {
  product: Product;
  quantity: number;
  selected: boolean;
}) {
  const { t } = useTrans(["shop"]);
  const { countryCode } = useLocale();
  const geMarked = useGeDisplayMarked(countryCode);
  const setDeal = useSetDeal(product.slug);
  if (!setDeal) return <RailCardPrice product={product} />;
  const { deal, basePrice, setPrice, savePercent } = setDeal;

  const caption = (
    <p className="text-sm tracking-[0.4px] text-red-60">
      {t("Buy {{count}}, save {{percent}}%", {
        count: deal.units,
        percent: savePercent,
      })}
    </p>
  );

  if (!selected || quantity < deal.units) {
    return (
      <div className="flex flex-col gap-1">
        <RailCardPrice product={product} />
        {caption}
      </div>
    );
  }

  const sets = Math.floor(quantity / deal.units);
  const rest = quantity % deal.units;
  const total = sets * saleOf(setPrice) + rest * saleOf(basePrice);
  const separately = quantity * saleOf(basePrice);
  const saved = formatMoney({
    amount: separately - total,
    currency: setPrice.currency,
    countryCode,
  });

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-1">
        <Badge
          // ★`key` = 금액 — 금액이 바뀌면 배지를 새로 만든다(GePrice 의 `key={text}` 와 같은 이유, #6047). GE 가 변환하며
          //   남긴 상태 위에 글자만 원래 금액(`Save $N`)으로 바뀌면 재변환 전까지 보이거나, GE 감시 설정에 따라 다시
          //   변환되지 않을 수 있다 — 새로 붙은 요소는 GE 가 다시 처리한다.
          key={saved}
          variant={"sale"}
          size={"sm"}
          className="tracking-[0.1px]"
          {...geTextProps(geMarked)}
          {...localeDependentProps}
        >
          {t("Save {{amount}}", {
            amount: saved,
            defaultValue: `Save ${saved}`,
          })}
        </Badge>
        <GePrice
          className="text-lg font-600 tracking-[0.5px] text-coolgray-90"
          amount={total}
          currency={setPrice.currency}
          countryCode={countryCode}
        />
        <GePrice
          className="text-lg tracking-[0.5px] text-coolgray-50 line-through"
          amount={separately}
          currency={setPrice.currency}
          countryCode={countryCode}
        />
      </div>
      {caption}
    </div>
  );
}
